import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Card, Input, Typography, LoadingIcon, IconButton } from '@/primer/components';
import { useAuth, useUpdateEmail, useUserAttributes, isSocialLogin } from '@/api';
import { PageLayout } from '@/components/layout';

/**
 * Settings — Password and email sub-screen.
 *
 * Allows non-social-login users to change their email address.
 * Logic is extracted from the original Settings page.
 *
 * Password reset is handled via the forgot-password flow on the login screen
 * and is not surfaced here.
 */
export function SettingsEmail() {
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();

  const { data: userAttributes, isLoading: isLoadingUserAttributes } = useUserAttributes();
  const canChangeEmail = !isLoadingUserAttributes && !isSocialLogin(userAttributes);
  const updateEmailMutation = useUpdateEmail();

  const [isEditing, setIsEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);

  const handleSave = async () => {
    setEmailError('');
    try {
      await updateEmailMutation.mutateAsync({
        email: newEmail,
        password: currentPassword,
      });
      await refreshSession();
      setEmailSuccess(true);
      setIsEditing(false);
      setNewEmail('');
      setCurrentPassword('');
      setTimeout(() => setEmailSuccess(false), 3000);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to update email');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewEmail('');
    setCurrentPassword('');
    setEmailError('');
  };

  const handleStartEdit = () => {
    setNewEmail(user?.email || '');
    setIsEditing(true);
  };

  if (isLoadingUserAttributes) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <LoadingIcon size="large" />
      </div>
    );
  }

  return (
    <PageLayout className="py-6">
      <div className="flex flex-col gap-6">
        {/* Back navigation */}
        <div className="md:hidden flex items-center gap-4">
          <IconButton
            icon="chevron-left"
            onClick={() => navigate({ to: '/settings' })}
            aria-label="Back to Settings"
          />
          <Typography variant="header" size="h3" as="h3">
            Settings
          </Typography>
        </div>

        <Typography variant="header" size="h1" as="h1">
          Password and email
        </Typography>

        {/* Current email card */}
        <Card>
          <div className="p-5 flex flex-col gap-4">
            <Typography variant="header" size="h2" as="h2">
              Email address
            </Typography>

            {emailSuccess && (
              <div className="bg-success rounded-small px-4 py-3">
                <Typography variant="body" size="small" color="success">
                  Email updated successfully!
                </Typography>
              </div>
            )}

            {emailError && (
              <div className="bg-error rounded-small px-4 py-3">
                <Typography variant="body" size="small" color="error">
                  {emailError}
                </Typography>
              </div>
            )}

            {isEditing ? (
              <div className="flex flex-col gap-4">
                <Input
                  label="New email address"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  autoComplete="email"
                />
                <Input
                  label="Current password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your password to confirm"
                  autoComplete="current-password"
                />
                <div className="flex gap-3">
                  <Button
                    fullWidth
                    onClick={handleSave}
                    isLoading={updateEmailMutation.isPending}
                    disabled={!newEmail || !currentPassword}
                  >
                    Save
                  </Button>
                  <Button variant="secondary" fullWidth onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1 min-w-0">
                  <Typography variant="body" size="medium" color="bodylighter">
                    Current email
                  </Typography>
                  <Typography variant="body" size="medium" className="truncate">
                    {user?.email}
                  </Typography>
                </div>
                {canChangeEmail && (
                  <Button variant="secondary" size="small" onClick={handleStartEdit}>
                    Change
                  </Button>
                )}
              </div>
            )}

            {!canChangeEmail && !isEditing && (
              <div className="bg-informative rounded-small px-4 py-3">
                <Typography variant="body" size="small" color="info">
                  Your account uses social login. Email changes are managed through your social
                  provider.
                </Typography>
              </div>
            )}
          </div>
        </Card>
      </div>
    </PageLayout>
  );
}

export default SettingsEmail;
